import _ from '#/lodash-extended';
import { valid as semverValid, gt as semverGt } from 'semver';
import app from '#/app';

const IS_ANGEL =
  new URL(window.location.href).searchParams.get('angel') !== null;
const MINUTES = 60e3;
const LOCK_NOTIFICATION_PERIOD = 30 * MINUTES;
let whenNotificationShouldAppear = undefined;
let whenLockShouldStart = undefined;
let lockNotificationId = null;
let updateNotificationId = null;

export const MaintenancePlugin = (store) => {
  app.broadcast.on('sw-maintenance-data', (e) => {
    const isChanged = !_.isEqual(
      e.reflection,
      JSON.parse(JSON.stringify(store.state.maintenanceState.data))
    );

    if (isChanged) {
      store.commit('setMaintenanceState', e.reflection);
      onChange(e.reflection, store.state.maintenanceState);
    }
  });

  window.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      // console.debug('👁');
      app.broadcast.emit('sw-maintenance-pull');
    }
  });
};

/*
 * Infographic of the maintenance lifecycle:
 * time axis ->
 * -----=====locked-----
 *  (1)  (2)   (3)  (4)
 *  (1) before notification period
 *       (2) within notification period
 *             (3) while locked
 *                  (4) lock ended
 */
function onChange(data, mState) {
  // console.debug('MaintenancePlugin::onChange', data);

  if (data.lock === true && !IS_ANGEL) {
    const now = Date.now();

    if (now >= data.lockTimestamp) {
      // (3)
      startLock();
    } else if (now >= data.lockTimestamp - LOCK_NOTIFICATION_PERIOD) {
      // (2)
      showLockNotification(mState);
    } else if (now < data.lockTimestamp - LOCK_NOTIFICATION_PERIOD) {
      // (1)
      if (lockNotificationId) {
        removeLockNotification();
      }
      const howLong = data.lockTimestamp - LOCK_NOTIFICATION_PERIOD - now;
      window.clearTimeout(whenNotificationShouldAppear);
      whenNotificationShouldAppear = window.setTimeout(() => {
        whenNotificationShouldAppear = undefined;
        showLockNotification(mState);
      }, howLong);
    }
  } else {
    // (4)
    if (whenNotificationShouldAppear) {
      window.clearTimeout(whenNotificationShouldAppear);
      whenNotificationShouldAppear = undefined;
    }
    if (lockNotificationId) {
      removeLockNotification();
    }
  }

  const isValid = data.version && semverValid(data.version);
  const hasNewVersion =
    isValid && semverGt(data.version, serverProfile.version);
  if (hasNewVersion) {
    showUpdateNotification();
  } else if (updateNotificationId) {
    removeUpdateNotification();
  }
}

function showLockNotification(mState) {
  if (lockNotificationId) {
    removeLockNotification();
  }

  lockNotificationId = app.guid();

  app.notificationController.dispatchEvent(
    {
      data: {
        event: 'LOCK_FOR_MAINTENANCE',
        persisted: true,
        notificationId: lockNotificationId,
        uniqueGroupName: 'LOCK_FOR_MAINTENANCE',
        lockTimestamp: mState.data.lockTimestamp,
      },
    },
    { eventOrigin: 'MaintenancePlugin' }
  );

  whenLockShouldStart = window.setTimeout(() => {
    removeLockNotification();
    startLock();
  }, mState.data.lockTimestamp - Date.now());
}

function removeLockNotification() {
  app.notificationController.remove({
    notificationId: lockNotificationId,
  });
  lockNotificationId = null;
  window.clearTimeout(whenLockShouldStart);
  whenLockShouldStart = undefined;
}

function startLock() {
  app.state.enableNavigation();
  // prevent iframe from blocking navigation in onbeforeunload by removing them
  document.getElementsByTagName('iframe').forEach((iframe) => {
    iframe.remove();
  });

  window.location = `/503?redirectUrl=${encodeURIComponent(
    window.location.href
  )}`;
}

function showUpdateNotification() {
  updateNotificationId = app.guid();
  app.notificationController.dispatchEvent(
    {
      data: {
        event: 'VERSION_UPDATE',
        persisted: true,
        notificationId: updateNotificationId,
        uniqueGroupName: 'VERSION_UPDATE',
      },
    },
    { eventOrigin: 'MaintenancePlugin' }
  );
}

function removeUpdateNotification() {
  app.notificationController.remove({
    notificationId: updateNotificationId,
  });
  updateNotificationId = null;
}
